/**
 * Capacity Assessment Card Component
 * Displays detailed capacity assessment for a staff member
 */

import React from 'react';
import { Card, Badge } from '@tracekit/core';
import {
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Info,
} from 'lucide-react';

interface CapacityData {
  staff_member_id: string;
  staff_name: string;
  current_projects: Array<{
    report_id?: string;
    report_name?: string;
    report_type?: string;
    project_type?: string;
    service_line?: string;
    role?: string;
    assigned_hours_per_week?: number;
    estimated_hours?: number;
  }>;
  upcoming_deadlines: Array<{
    opportunity_id: string;
    title: string;
    deadline: string;
    days_until: number;
  }>;
  proposal_assignments: number;
  capacity: {
    total_hours_per_week: number;
    total_fte: number;
    assigned_hours_per_week: number;
    available_hours_per_week: number;
    available_fte: number;
    utilization_percent: number;
  };
  warnings: {
    capacity_warning: boolean;
    capacity_exceeded: boolean;
  };
}

interface CapacityAssessmentCardProps {
  capacityData: CapacityData;
  showDetails?: boolean;
}

export function CapacityAssessmentCard({
  capacityData,
  showDetails = true,
}: CapacityAssessmentCardProps) {
  const { capacity, warnings, current_projects, upcoming_deadlines, proposal_assignments } = capacityData;
  
  const utilizationColor = 
    capacity.utilization_percent >= 100 ? 'bg-red-600' :
    capacity.utilization_percent >= 90 ? 'bg-yellow-600' :
    capacity.utilization_percent >= 75 ? 'bg-blue-600' :
    'bg-green-600';

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Capacity Assessment
        </h3>
        {warnings.capacity_exceeded && (
          <Badge variant="error" className="flex items-center gap-1">
            <XCircle className="w-4 h-4" />
            Over-Allocated
          </Badge>
        )}
        {warnings.capacity_warning && !warnings.capacity_exceeded && (
          <Badge variant="warning" className="flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            Warning
          </Badge>
        )}
        {!warnings.capacity_warning && !warnings.capacity_exceeded && (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4" />
            Available
          </Badge>
        )}
      </div>

      {/* Capacity Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Capacity</div>
          <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
            {capacity.total_hours_per_week}h
          </div>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            {capacity.total_fte} FTE
            <div className="group relative">
              <Info className="w-3 h-3 inline cursor-help" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10 w-48 p-2 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded shadow-lg">
                40 hours/week = 1.0 FTE (Full-Time Equivalent)
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Assigned</div>
          <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
            {capacity.assigned_hours_per_week.toFixed(1)}h
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {((capacity.assigned_hours_per_week / capacity.total_hours_per_week) * 100).toFixed(1)}% utilized
          </div>
        </div>

        <div className={`p-3 rounded-lg ${
          capacity.available_hours_per_week < 10 
            ? 'bg-red-50 dark:bg-red-900/20' 
            : 'bg-green-50 dark:bg-green-900/20'
        }`}>
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Available</div>
          <div className={`text-2xl font-bold ${
            capacity.available_hours_per_week < 10
              ? 'text-red-700 dark:text-red-300'
              : 'text-green-700 dark:text-green-300'
          }`}>
            {capacity.available_hours_per_week.toFixed(1)}h
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {capacity.available_fte.toFixed(2)} FTE
          </div>
        </div>

        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Utilization</div>
          <div className={`text-2xl font-bold ${
            capacity.utilization_percent >= 100 ? 'text-red-700 dark:text-red-300' :
            capacity.utilization_percent >= 90 ? 'text-yellow-700 dark:text-yellow-300' :
            capacity.utilization_percent >= 75 ? 'text-blue-700 dark:text-blue-300' :
            'text-green-700 dark:text-green-300'
          }`}>
            {capacity.utilization_percent.toFixed(1)}%
          </div>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            {capacity.utilization_percent >= 100 ? (
              <TrendingUp className="w-3 h-3 text-red-600" />
            ) : capacity.utilization_percent >= 90 ? (
              <AlertTriangle className="w-3 h-3 text-yellow-600" />
            ) : (
              <TrendingDown className="w-3 h-3 text-green-600" />
            )}
            {capacity.utilization_percent >= 100 ? 'Over' : capacity.utilization_percent >= 90 ? 'High' : 'Normal'}
          </div>
        </div>
      </div>

      {/* Utilization Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Capacity Utilization
          </span>
          <span className={`text-sm font-bold ${
            capacity.utilization_percent >= 100 ? 'text-red-600' :
            capacity.utilization_percent >= 90 ? 'text-yellow-600' :
            'text-gray-600'
          }`}>
            {capacity.utilization_percent.toFixed(1)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 relative overflow-hidden">
          <div
            className={`h-4 rounded-full transition-all ${utilizationColor}`}
            style={{ width: `${Math.min(100, capacity.utilization_percent)}%` }}
          />
          {capacity.utilization_percent > 100 && (
            <div className="absolute right-0 top-0 h-4 w-4 bg-red-600 animate-pulse" />
          )}
        </div>
        <div className="relative mt-1 text-xs text-gray-500" style={{ height: '20px' }}>
          <span className="absolute left-0">0%</span>
          <span className="absolute text-yellow-600 font-medium" style={{ left: '90%', transform: 'translateX(-50%)' }}>90% Warning</span>
          <span className="absolute text-red-600 font-medium" style={{ left: '100%', transform: 'translateX(-100%)' }}>100% Limit</span>
          <span className="absolute right-0">100%+</span>
        </div>
      </div>

      {/* Warnings */}
      {warnings.capacity_exceeded && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-900 dark:text-red-200 mb-1">
                Capacity Exceeded
              </h4>
              <p className="text-sm text-red-800 dark:text-red-300">
                Staff is over-allocated at {capacity.utilization_percent.toFixed(1)}% utilization. 
                This exceeds the 100% capacity threshold. Consider reassigning work or requesting additional staff.
              </p>
            </div>
          </div>
        </div>
      )}

      {warnings.capacity_warning && !warnings.capacity_exceeded && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-1">
                Capacity Warning
              </h4>
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                Staff is {capacity.utilization_percent.toFixed(1)}% allocated, approaching the 90% warning threshold. 
                Monitor capacity and consider workload adjustments.
              </p>
            </div>
          </div>
        </div>
      )}

      {showDetails && (
        <>
          {/* Current Projects */}
          {current_projects.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Current Projects ({current_projects.length})
              </h4>
              <div className="space-y-2">
                {current_projects.map((project, idx) => (
                  <div key={project.report_id || idx} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="font-medium text-gray-900 dark:text-theme-text">
                      {project.report_name || project.project_type || 'Unknown Project'}
                    </div>
                    {project.service_line && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Service Line: {project.service_line}
                      </div>
                    )}
                    {project.role && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Role: {project.role}
                      </div>
                    )}
                    {project.assigned_hours_per_week && (
                      <div className="text-xs text-gray-500 mt-1">
                        {project.assigned_hours_per_week}h/week
                      </div>
                    )}
                    {project.estimated_hours && !project.assigned_hours_per_week && (
                      <div className="text-xs text-gray-500 mt-1">
                        Estimated: {project.estimated_hours} hours
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Deadlines */}
          {upcoming_deadlines.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                Upcoming Deadlines ({upcoming_deadlines.length})
              </h4>
              <div className="space-y-2">
                {upcoming_deadlines.slice(0, 5).map((deadline, idx) => {
                  const isUrgent = deadline.days_until <= 7;
                  const isSoon = deadline.days_until <= 30;
                  
                  return (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border ${
                        isUrgent
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                          : isSoon
                          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                      }`}
                    >
                      <div className="font-medium text-gray-900 dark:text-theme-text">
                        {deadline.title}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {new Date(deadline.deadline).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </div>
                      <div className={`text-xs font-medium mt-1 ${
                        isUrgent ? 'text-red-700 dark:text-red-300' :
                        isSoon ? 'text-yellow-700 dark:text-yellow-300' :
                        'text-blue-700 dark:text-blue-300'
                      }`}>
                        {deadline.days_until === 0
                          ? 'Due today!'
                          : deadline.days_until === 1
                          ? '1 day remaining'
                          : `${deadline.days_until} days remaining`}
                      </div>
                    </div>
                  );
                })}
                {upcoming_deadlines.length > 5 && (
                  <div className="text-xs text-gray-500 text-center mt-2">
                    +{upcoming_deadlines.length - 5} more deadlines
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Proposal Assignments */}
          {proposal_assignments > 0 && (
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-purple-600" />
                Proposal Assignments
              </h4>
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="text-lg font-bold text-purple-700 dark:text-purple-300">
                  {proposal_assignments} active proposal{proposal_assignments !== 1 ? 's' : ''}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Staff is assigned to {proposal_assignments} proposal{proposal_assignments !== 1 ? 's' : ''} with upcoming deadlines
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
